import * as React from 'react';
import { ChangeEventHandler, useMemo } from 'react';
import { useTheme } from 'styled-components';

import { GapSizeType } from '../../../../theme/type/gap-type';
import { SwitchType } from '../../../../theme/type/switch-type';
import { cn, marginBottomClass, marginTopClass } from '../../../../utils/tailwind';

interface SwitchPropsInterface {
    name?: string;
    iconOff?: React.ReactNode;
    iconOn?: React.ReactNode;
    disabled?: boolean;
    onChange: ChangeEventHandler<HTMLInputElement>;
    checked?: boolean;
    label?: React.ReactNode;
    marginBottom?: GapSizeType;
    marginTop?: GapSizeType;
    variant?: SwitchType;
}

export const Switch: React.FC<SwitchPropsInterface> = ({
    name,
    disabled,
    onChange,
    iconOn,
    iconOff,
    checked = false,
    label,
    marginBottom,
    marginTop,
    variant = 'primary',
}: SwitchPropsInterface) => {
    const theme = useTheme();
    const icon = useMemo(() => {
        if (iconOn && iconOff) {
            return checked ? iconOn : iconOff;
        }
        return null;
    }, [iconOn, iconOff]);

    const variantTheme = theme.switchCheckbox[variant];

    return (
        <div
            className={cn(
                'flex items-center font-medium text-text',
                marginBottom ? marginBottomClass[marginBottom] : '',
                marginTop ? marginTopClass[marginTop] : '',
            )}
        >
            <label
                className="relative flex h-[2.4rem] w-[4rem] cursor-pointer rounded-[1.2rem]"
                style={{
                    background: checked ? variantTheme.background.on : variantTheme.background.off,
                }}
            >
                <input
                    name={name}
                    id={name}
                    type="checkbox"
                    onChange={(e) => onChange(e)}
                    disabled={disabled}
                    checked={checked}
                    className="hidden"
                />
                <div
                    className="absolute top-1/2 flex h-[1.6rem] w-[1.6rem] -translate-y-1/2 items-center justify-center rounded-full bg-white transition-transform [&_i]:h-[1rem] [&_i]:min-w-[1rem] [&_i]:w-[1rem]"
                    style={{
                        transform: checked ? 'translate(2rem, -50%)' : 'translate(0.4rem, -50%)',
                        color: checked ? variantTheme.icon.on : variantTheme.icon.off,
                    }}
                >
                    {icon}
                </div>
            </label>
            {label && (
                <label htmlFor={name} className="ml-[1rem] cursor-pointer">
                    {label}
                </label>
            )}
        </div>
    );
};
